import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableHead, TableRow, Chip, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Alert, InputAdornment, IconButton,
} from '@mui/material';
import { Inventory, Search, Save, CheckCircle, Warning } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

export default function StocktakePage() {
  const dispatch = useDispatch();
  const { user } = useSelector(s => s.auth);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [search, setSearch] = useState('');
  const [counts, setCounts] = useState({}); // { product_id: counted_qty }
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/inventory', { params: { limit: 200, branch_id: branchId } });
      setProducts(data.data || []);
      // Pre-fill counts with system qty so unchanged rows are skipped
      const init = {};
      (data.data || []).forEach(p => { init[p.id] = p.stock; });
      setCounts(init);
    } finally { setLoading(false); }
  }, [branchId]);

  useEffect(() => {
    api.get('/branches').then(r => {
      const brs = r.data.data || [];
      setBranches(brs);
      if (brs.length > 0) setBranchId(String(brs[0].id));
    }).catch(() => {
      // Fallback: use user's branch
      if (user?.branch_id) setBranchId(String(user.branch_id));
    });
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const filtered = products.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.sku || '').toLowerCase().includes(search.toLowerCase())
  );

  const setCount = (productId, val) => {
    setCounts(prev => ({ ...prev, [productId]: val === '' ? '' : Math.max(0, +val) }));
  };

  const getDiff = (p) => {
    const counted = counts[p.id];
    if (counted === '' || counted === undefined) return 0;
    return +counted - +p.stock;
  };

  const changedCount = filtered.filter(p => getDiff(p) !== 0).length;

  const submitStocktake = async () => {
    const changed = products.filter(p => getDiff(p) !== 0);
    if (!changed.length) {
      dispatch(showSnackbar({ message: 'No changes to submit', severity: 'info' }));
      return;
    }
    setSubmitting(true);
    try {
      const payload = changed.map(p => ({
        product_id: p.id,
        branch_id: +branchId,
        counted_qty: counts[p.id] === '' ? +p.stock : +counts[p.id],
      }));
      const { data } = await api.post('/inventory/stocktake', { counts: payload, notes: `Stocktake via POS — ${new Date().toLocaleDateString('en-PK')}` });
      setResult(data.data);
      dispatch(showSnackbar({ message: `Stocktake complete — ${data.data.adjusted} adjustments`, severity: 'success' }));
      load(); // reload fresh system qtys
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Stocktake failed', severity: 'error' }));
    } finally { setSubmitting(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Inventory color="primary" />
          <Typography variant="h5" fontWeight={700}>Physical Stocktake / گنتی</Typography>
        </Box>
        <Button
          variant="contained" color="success" size="large"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Save />}
          onClick={submitStocktake} disabled={submitting || !branchId || changedCount === 0}
        >
          {submitting ? 'Submitting…' : `Submit Stocktake (${changedCount} changes)`}
        </Button>
      </Box>

      {result && (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}
          onClose={() => setResult(null)}>
          Stocktake completed — <strong>{result.adjusted}</strong> product(s) adjusted
        </Alert>
      )}

      {/* Controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pb: '16px !important' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Branch</InputLabel>
            <Select value={branchId} onChange={e => { setBranchId(e.target.value); setResult(null); }} label="Branch">
              {branches.map(b => <MenuItem key={b.id} value={String(b.id)}>{b.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" placeholder="Search product / SKU…" value={search}
            onChange={e => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={`${filtered.length} products`} variant="outlined" />
            {changedCount > 0 && <Chip label={`${changedCount} changes`} color="warning" />}
          </Box>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Alert severity="info" sx={{ mb: 2 }}>
        Enter the <strong>physical count</strong> for each item. Rows highlighted in orange have discrepancies. Submit to update inventory and record adjustments.
      </Alert>

      <Card>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>System Qty</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Physical Count</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Variance</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {!branchId ? 'Select a branch to begin stocktake' : 'No products found'}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map(p => {
                const diff = getDiff(p);
                const hasChange = diff !== 0;
                return (
                  <TableRow key={p.id}
                    sx={{ bgcolor: hasChange ? (diff < 0 ? '#fef2f2' : '#f0fdf4') : 'inherit' }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace" color="text.secondary">{p.sku || '—'}</Typography>
                    </TableCell>
                    <TableCell>{p.category || '—'}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600}>{fmt(p.stock)}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 120 }}>
                      <TextField
                        size="small" type="number" value={counts[p.id] ?? p.stock}
                        onChange={e => setCount(p.id, e.target.value)}
                        inputProps={{ min: 0, style: { textAlign: 'center', fontWeight: 700 } }}
                        sx={{
                          width: 100,
                          '& .MuiOutlinedInput-root': {
                            bgcolor: hasChange ? (diff < 0 ? '#fecaca' : '#bbf7d0') : 'transparent',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {hasChange ? (
                        <Typography fontWeight={700} color={diff > 0 ? 'success.main' : 'error.main'}>
                          {diff > 0 ? '+' : ''}{fmt(diff)}
                        </Typography>
                      ) : (
                        <Typography color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {hasChange ? (
                        <Chip
                          icon={<Warning />}
                          label={diff > 0 ? 'Surplus' : 'Shortage'}
                          size="small"
                          color={diff > 0 ? 'success' : 'error'}
                          variant="outlined"
                        />
                      ) : (
                        <Chip label="Match" size="small" color="default" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </Box>
  );
}
